/**
 * Adds a passkey: a name, the browser's prompt, and a sign-in again when the
 * server asks for one. Used on the passkeys page and, for someone who has none
 * yet, on the signing page before they sign.
 *
 * The browser prompt only ever starts from a click, with nothing awaited in
 * between: WebKit refuses `navigator.credentials.create` once a network round
 * trip has used up the click. So the registration options are fetched as soon
 * as the form shows, kept fresh while it waits, and fetched again after a
 * sign-in again (the server ties them to the session). See ./enrolment.ts.
 */
import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { TbFingerprint } from "react-icons/tb";

import FieldLabel from "components/ds/FieldLabel";
import Input from "components/ds/Input";
import PloyButton from "components/ds/PloyButton";
import {
  createAttestation,
  defaultPasskeyLabel,
  normalisePasskeyLabel,
  PASSKEY_LABEL_MAX,
  webAuthnAvailable,
} from "modules/members/webauthn";
import {
  savePasskey,
  startPasskeyRegistration,
  useInvalidatePasskeys,
  type MFPasskey,
} from "state/members/passkeys";

import {
  completeEnrolment,
  isFresh,
  prepareRegistration,
  REGISTRATION_MAX_AGE_MS,
  type EnrolOutcome,
  type PrepareOutcome,
  type PreparedRegistration,
} from "./enrolment";
import ReauthPrompt from "./ReauthPrompt";

export const UNSUPPORTED_MESSAGE =
  "This browser cannot make passkeys. Try a recent version of Chrome, Safari, Edge or Firefox.";
export const ADD_REAUTH_MESSAGE =
  "To add a passkey, confirm it's you by signing in again.";

const browserLabel = () =>
  defaultPasskeyLabel(typeof navigator !== "undefined" ? navigator.userAgent : "");

const ENROL_DEPS = { create: createAttestation, save: savePasskey };

export type PasskeyEnrolmentProps = {
  onCreated: (passkey: MFPasskey) => void;
  onCancel?: () => void;
  /** The main button's label. */
  submitLabel?: string;
};

/** `reauth_still`: asking to sign in again, straight after a sign-in the server did not accept. */
type Stage = "form" | "working" | "reauth" | "reauth_still";

const PasskeyEnrolment = ({
  onCreated,
  onCancel,
  submitLabel = "Create passkey",
}: PasskeyEnrolmentProps) => {
  const invalidatePasskeys = useInvalidatePasskeys();
  const supported = webAuthnAvailable();
  const [fallbackLabel] = useState(browserLabel);
  const [label, setLabel] = useState(fallbackLabel);
  const [stage, setStage] = useState<Stage>("form");
  const [error, setError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<PreparedRegistration | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [signedInAgain, setSignedInAgain] = useState(false);
  // True from signing in again until the server first accepts the session, so
  // a `reauth_required` straight after says the sign-in did not help. One
  // later on means the fresh-sign-in window ran out, which is no one's fault.
  const afterReauth = useRef(false);
  const mounted = useRef(true);

  const askToSignInAgain = () =>
    setStage(afterReauth.current ? "reauth_still" : "reauth");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Fetches registration options. A background refresh (`quiet`) keeps the
   * old options and says nothing if it fails; the click then fetches anew.
   */
  const prepare = async (quiet = false): Promise<PrepareOutcome> => {
    setPreparing(true);
    const outcome = await prepareRegistration(startPasskeyRegistration);
    if (!mounted.current) return outcome;
    setPreparing(false);
    if (outcome.kind === "ready") {
      afterReauth.current = false;
      setPrepared(outcome.prepared);
    } else if (outcome.kind === "reauth") {
      setPrepared(null);
      setSignedInAgain(false);
      askToSignInAgain();
    } else if (!quiet) {
      setError(outcome.message);
    }
    return outcome;
  };

  // Ready the options as soon as the form shows, so the click can go straight
  // to the browser prompt.
  useEffect(() => {
    // Once, on mount; later fetches follow the person's actions.
    if (supported) prepare();
  }, []);

  // Keep them fresh while the form waits: the server's token lives two minutes.
  useEffect(() => {
    if (!prepared || stage !== "form") return undefined;
    const wait = Math.max(
      0,
      prepared.fetchedAt + REGISTRATION_MAX_AGE_MS - Date.now(),
    );
    const id = window.setTimeout(() => prepare(true), wait);
    return () => window.clearTimeout(id);
  }, [prepared, stage]);

  if (!supported) {
    return (
      <Text role="alert" fontSize="smd" color="neutral.800">
        {UNSUPPORTED_MESSAGE}
      </Text>
    );
  }

  const settle = async (pending: Promise<EnrolOutcome>) => {
    const outcome = await pending;
    if (!mounted.current) return;
    switch (outcome.kind) {
      case "created":
        invalidatePasskeys();
        onCreated(outcome.passkey);
        return;
      case "reauth":
        askToSignInAgain();
        return;
      case "cancelled":
        setStage("form");
        prepare(true);
        return;
      case "failed":
        setError(outcome.message);
        setStage("form");
        // A token is good for one attempt; have a fresh one ready for the next.
        prepare(true);
    }
  };

  const onCreate = () => {
    if (stage === "working") return;
    const name = normalisePasskeyLabel(label, fallbackLabel);
    setError(null);
    setSignedInAgain(false);
    setStage("working");

    if (isFresh(prepared, Date.now())) {
      setPrepared(null);
      // `create` runs inside this call, still within the click.
      settle(completeEnrolment(prepared, name, ENROL_DEPS));
      return;
    }

    // No options with enough life left for a prompt (the fetch failed, or the
    // tab slept). Fetch them now;
    // the browser may then refuse the prompt for want of the click, which
    // reads as "the prompt closed", and the next click has fresh options.
    prepare().then((outcome) => {
      if (!mounted.current) return;
      if (outcome.kind !== "ready") {
        if (outcome.kind === "failed") setStage("form");
        return;
      }
      setPrepared(null);
      settle(completeEnrolment(outcome.prepared, name, ENROL_DEPS));
    });
  };

  if (stage === "reauth" || stage === "reauth_still") {
    return (
      <ReauthPrompt
        message={ADD_REAUTH_MESSAGE}
        stillRequired={stage === "reauth_still"}
        onSignedIn={() => {
          afterReauth.current = true;
          setSignedInAgain(true);
          setStage("form");
          // The options belong to the session, and the session just changed.
          prepare();
        }}
        onCancel={() => {
          afterReauth.current = false;
          setStage("form");
        }}
      />
    );
  }

  const working = stage === "working";
  const gettingReady = preparing && !prepared && !working;

  return (
    <Stack spacing="md">
      <Box>
        <FieldLabel>Name</FieldLabel>
        <Input
          aria-label="Passkey name"
          value={label}
          maxLength={PASSKEY_LABEL_MAX}
          isDisabled={working}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Text fontSize="xs" color="neutral.700" mt="1xs">
          So you can tell your passkeys apart. You can change it later.
        </Text>
      </Box>

      {signedInAgain && (
        <Text fontSize="smd" color="success.700" fontWeight={500}>
          You are signed in again. Create your passkey to finish.
        </Text>
      )}

      {error && (
        <Text role="alert" fontSize="smd" fontWeight={500} color="error.700">
          {error}
        </Text>
      )}

      <Flex gap="sm" wrap="wrap">
        <PloyButton
          variant="primary"
          size="md"
          onClick={onCreate}
          isLoading={working || gettingReady}
          loadingText={working ? "Waiting for your browser" : "Getting ready"}
          leftIcon={<TbFingerprint size={16} aria-hidden />}
        >
          {submitLabel}
        </PloyButton>
        {onCancel && (
          <PloyButton
            variant="ghost"
            size="md"
            onClick={onCancel}
            isDisabled={working}
          >
            Cancel
          </PloyButton>
        )}
      </Flex>
    </Stack>
  );
};

export default PasskeyEnrolment;
